'use client'

import React, { useEffect } from 'react'
import { usePathname } from 'next/navigation'

import { isAeDomain } from '@/src/utils/generalUtils/isAeDomain'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import useRedirectToLogin from '@/src/hooks/navigation/useRedirectToLogin'

const SESSION_STORAGE_KEY = 'homepageLoginPopupShown'

/**
 * HomepageLoginAutoShow Component
 * 
 * Automatically displays login popup on homepage (ae.flyin.com) for new user sessions.
 * - Only shows once per session
 * - Only on homepage routes (/, /en, /ar)
 * - Only on ae.flyin.com domain
 * - Only if user is not logged in
 */
const HomepageLoginAutoShow: React.FC = () => {
    const pathname = usePathname()
    const { SignInModal, setSignInModalOpen } = useRedirectToLogin()

    useEffect(() => {
        // Only run on client-side
        if (typeof window === 'undefined') return

        // Check if on homepage (root or locale-only path)
        const isHomepage = pathname === '/' || /^\/[a-z]{2}$/.test(pathname)

        // Check if on ae.flyin.com domain
        const isAe = isAeDomain()

        // Check if user is logged in
        const isLoggedIn = isUserSignedIn()

        // Check if popup was already shown in this session
        const wasShown = sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true'

        // Auto-show conditions: homepage + ae domain + not logged in + not shown yet
        if (isHomepage && isAe && !isLoggedIn && !wasShown && setSignInModalOpen) {
            setSignInModalOpen(true)
            sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
        }
    }, [pathname, setSignInModalOpen])

    // Always render modal (it controls its own visibility via 'open' prop)
    return <SignInModal />
}

export { HomepageLoginAutoShow }
export default HomepageLoginAutoShow

